import React, { useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { i18n } from "../../translate/i18n";
import makeStyles from "@material-ui/core/styles/makeStyles";
import * as XLSX from "xlsx";
const { read, utils } = XLSX;
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import FilterListIcon from "@material-ui/icons/FilterList";
import ClearIcon from "@material-ui/icons/Clear";
import { UploadCloud } from "lucide-react";
import { useTheme } from "@material-ui/core/styles";
import CustomToggleSwitch from "../CustomToggleSwitch";
import api from "../../services/api";
import { useHistory } from "react-router-dom";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

function WorksheetToDatagrid(ws) {
  const rows = utils.sheet_to_json(ws, { header: 1, defval: "" });
  const range = utils.decode_range(ws["!ref"] || "A1");
  const columns = Array.from({ length: range.e.c + 1 }, (_, i) => ({
    key: String(i),
    name: utils.encode_col(i),
  }));
  return { rows, columns };
}

const MAX_CELL_CHARS = 40;

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";
  const surface = dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa");
  const border = dsColors.border || (isDark ? "#202632" : "#e8e8e8");
  const borderLight = dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0");
  const textPrimary = dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21");
  const textSecondary = dsColors.textSecondary || (isDark ? "#8696a0" : "#667781");
  const surfaceHover = dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6");
  return ({
  tableWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    border: `1px solid ${border}`,
    borderRadius: 12,
    background: surface,
    ...theme.scrollbarStyles,
  },
  excelTable: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 13.5,
    color: textPrimary,
    "& th, & td": {
      borderBottom: `1px solid ${borderLight}`,
      borderRight: "none",
      padding: "10px 14px",
      textAlign: "left",
      whiteSpace: "nowrap",
    },
  },
  headerRow: {
    "& th": {
      background: isDark ? "#1c1f27" : "#eef1f5",
      position: "sticky",
      top: 0,
      zIndex: 3,
      fontWeight: 700,
      fontSize: 11.5,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: textSecondary,
      borderBottom: `1px solid ${border}`,
      padding: "12px 14px",
    },
  },
  selectRow: {
    background: isDark ? "rgba(255,255,255,0.015)" : "rgba(15,23,42,0.02)",
    position: "sticky",
    top: 41,
    zIndex: 2,
    "& th": {
      fontWeight: 400,
      padding: "6px 10px",
    },
  },
  filterRow: {
    background: isDark ? "rgba(33,150,243,0.05)" : "rgba(33,150,243,0.04)",
    position: "sticky",
    top: 79,
    zIndex: 2,
    "& th": {
      padding: "6px 10px",
    },
  },
  dataRow: {
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: surfaceHover,
    },
  },
  selectedRow: {
    backgroundColor: `${isDark ? "rgba(33,150,243,0.16)" : "rgba(33,150,243,0.10)"} !important`,
  },
  cellText: {
    display: "inline-block",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    cursor: "default",
  },
  checkboxCell: {
    width: 40,
    textAlign: "center",
    padding: "4px !important",
  },
  cyberpunkCheckbox: {
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    width: 16,
    height: 16,
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 5,
    backgroundColor: "transparent",
    display: "inline-block",
    position: "relative",
    margin: 0,
    padding: 0,
    cursor: "pointer",
    flexShrink: 0,
    verticalAlign: "middle",
    "&::before": {
      content: '""',
      backgroundColor: theme.palette.primary.main,
      display: "block",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%) scale(0)",
      width: 8,
      height: 8,
      borderRadius: 3,
      transition: "all 0.25s ease-in-out",
    },
    "&:checked::before": {
      transform: "translate(-50%, -50%) scale(1)",
    },
    "&:indeterminate::before": {
      transform: "translate(-50%, -50%) scale(1)",
      width: 10,
      height: 2,
      borderRadius: 1,
    },
  },
  selectField: {
    fontSize: 12,
    minWidth: 130,
    "& .MuiSelect-select": {
      padding: "4px 8px",
    },
  },
  filterInput: {
    fontSize: 12,
    "& input": {
      padding: "4px 8px",
      fontSize: 12,
    },
  },
  filterActive: {
    color: theme.palette.primary.main,
  },
  actions: {
    flexShrink: 0,
    padding: "12px 14px",
    borderRadius: 12,
    border: `1px solid ${border}`,
    background: surface,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
  },
  importOptions: {
    flexShrink: 0,
    padding: "10px 14px",
    borderRadius: 12,
    border: `1px solid ${border}`,
    background: surface,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  backButtonContainer: {
    textAlign: "center",
    marginTop: 20,
  },
  filterChips: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    padding: "4px 8px",
    fontSize: 12,
    color: "#555",
    alignItems: "center",
  },
  filterChip: {
    background: "#e3ecfa",
    borderRadius: 4,
    padding: "2px 8px",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
  },
  });
});

const ContactImport = () => {
  const t = i18n.t.bind(i18n);
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark" || theme.mode === "dark";

  const [rows, setRows] = useState(null);
  const [columns, setColumns] = useState(null);
  const [columnValue, setColumnValue] = useState({});
  const [selectedFields, setSelectedFields] = useState({});
  const [openingFile, setOpeningFile] = useState(false);
  const [selection, setSelection] = useState({});
  const [invalidFile, setInvalidFile] = useState(false);
  const [error, setError] = useState(null);
  const [countCreated, setCountCreated] = useState(0);
  const [countIgnored, setCountIgnored] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [imported, setImported] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [validateContact, setValidateContact] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});

  const contactFields = [
    { id: "name", label: t("contactImport.fields.name"), required: true },
    { id: "number", label: t("contactImport.fields.number"), required: true },
    { id: "email", label: t("contactImport.fields.email"), required: false },
    { id: "tags", label: t("contactImport.fields.tags"), required: false },
    { id: "followUp", label: t("contactImport.fields.followUp"), required: false },
    { id: "productName", label: t("contactImport.fields.productName"), required: false },
    { id: "productStatus", label: t("contactImport.fields.productStatus"), required: false },
  ];

  // Filtra as linhas de dados (exclui header = row 0) com base nos filtros por coluna
  const filteredDataRows = useMemo(() => {
    if (!rows || rows.length <= 1) return [];
    const dataRows = rows.slice(1).map((row, i) => ({ row, originalIndex: i + 1 }));

    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v && v.trim());
    if (activeFilters.length === 0) return dataRows;

    return dataRows.filter(({ row }) =>
      activeFilters.every(([colIdx, filterText]) => {
        const cellVal = String(row[Number(colIdx)] || "").toLowerCase();
        return cellVal.includes(filterText.toLowerCase());
      })
    );
  }, [rows, columnFilters]);

  const handleFilterChange = (colIndex, value) => {
    setColumnFilters((prev) => ({ ...prev, [colIndex]: value }));
  };

  const clearFilter = (colIndex) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[colIndex];
      return next;
    });
  };

  const clearAllFilters = () => setColumnFilters({});

  const activeFilterCount = Object.values(columnFilters).filter((v) => v && v.trim()).length;

  const processImport = async () => {
    setUploading(true);

    if (!selection.number) {
      toastError(t("contactImport.validation.noNumberField"));
      setUploading(false);
      return;
    }
    if (!selection.name) {
      toastError(t("contactImport.validation.noNameField"));
      setUploading(false);
      return;
    }
    if (Object.keys(selectedRows).length === 0) {
      toastError(t("contactImport.validation.noContactsSelected"));
      setUploading(false);
      return;
    }

    let created = 0;
    let ignored = 0;

    for (const { row: item, originalIndex } of filteredDataRows) {
      if (!selectedRows[originalIndex]) continue;

      const contactData = {};
      for (let ci = 0; ci < columns.length; ci++) {
        const field = columnValue[columns[ci].key];
        if (field) contactData[field] = item[ci];
      }

      const missingRequired = contactFields.some((f) => f.required && !contactData[f.id]);
      if (missingRequired) {
        ignored++;
        continue;
      }

      try {
        const payload = { ...contactData, validateContact: validateContact ? "true" : "false" };
        delete payload.productName;
        delete payload.productStatus;
        if (contactData.productName) {
          payload.productName = contactData.productName;
          payload.productStatus = contactData.productStatus || "";
        }
        const res = await api.post("/contactsImport", payload);
        if (res.status === 200) created++;
        else ignored++;
      } catch {
        ignored++;
      }
    }

    setCountCreated(created);
    setCountIgnored(ignored);
    setValidateContact(false);
    setSelectedRows({});
    setImported(true);
    setUploading(false);

    if (ignored === 0) {
      toast.success(t("contactImport.messages.successComplete"));
    } else {
      toast.warn(t("contactImport.messages.successWithErrors"));
    }
  };

  const normalize = (s) =>
    String(s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .trim();

  const onChangeFile = (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setOpeningFile(true);
    setInvalidFile(false);
    setImported(false);
    setUploading(false);
    setColumnFilters({});
    const file = acceptedFiles[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = read(e.target.result);
        const ws = wb.Sheets[wb.SheetNames[0]];
        const { rows, columns } = WorksheetToDatagrid(ws);
        setRows(rows);
        setColumns(columns);

        // Auto-map: a primeira linha do arquivo contém os nomes das colunas
        // do modelo. Tentamos casar cada cabeçalho com um contactField pelo
        // id ou pela label normalizada (case-insensitive, sem acentos).
        const headerRow = rows[0] || [];
        const autoMap = {};
        const fieldByKey = new Map();
        contactFields.forEach((f) => {
          fieldByKey.set(normalize(f.id), f.id);
          fieldByKey.set(normalize(f.label), f.id);
        });
        columns.forEach((col, i) => {
          const cellText = normalize(headerRow[i]);
          if (cellText && fieldByKey.has(cellText)) {
            autoMap[col.key] = fieldByKey.get(cellText);
          }
        });
        setColumnValue(autoMap);

        // Pré-seleciona todas as linhas de dados pra facilitar a importação
        const initialSelected = {};
        rows.slice(1).forEach((_, i) => { initialSelected[i + 1] = true; });
        setSelectedRows(initialSelected);

        setOpeningFile(false);
      } catch (err) {
        console.error(err);
        setInvalidFile(true);
        setOpeningFile(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSelectChange = (event) => {
    const newValue = event.target.value;
    const columnKey = event.target.name;

    if (columnValue[columnKey]) {
      const oldValue = columnValue[columnKey];
      setSelectedFields((prev) => { const n = { ...prev }; delete n[oldValue]; return n; });
    }

    if (newValue === "") {
      setColumnValue((prev) => { const n = { ...prev }; delete n[columnKey]; return n; });
      setSelection((prev) => {
        const n = { ...prev };
        Object.keys(n).forEach((k) => { if (n[k] === columnKey) delete n[k]; });
        return n;
      });
      return;
    }

    if (selectedFields[newValue]) {
      const fieldLabel = contactFields.find((f) => f.id === newValue)?.label || newValue;
      toastError(t("contactImport.validation.fieldAlreadySelected", { field: fieldLabel }));
      return;
    }

    setSelection((prev) => ({ ...prev, [newValue]: columnKey }));
    setSelectedFields((prev) => ({ ...prev, [newValue]: columnKey }));
    setColumnValue((prev) => ({ ...prev, [columnKey]: newValue }));
  };

  // Select all visíveis (filtrados)
  const allVisibleSelected = filteredDataRows.length > 0 && filteredDataRows.every(({ originalIndex }) => selectedRows[originalIndex]);

  const handleSelectAll = (checked) => {
    if (checked) {
      const newSelected = { ...selectedRows };
      filteredDataRows.forEach(({ originalIndex }) => { newSelected[originalIndex] = true; });
      setSelectedRows(newSelected);
    } else {
      const newSelected = { ...selectedRows };
      filteredDataRows.forEach(({ originalIndex }) => { delete newSelected[originalIndex]; });
      setSelectedRows(newSelected);
    }
  };

  const truncate = (text, max = MAX_CELL_CHARS) => {
    const str = String(text || "");
    return str.length > max ? str.substring(0, max) + "…" : str;
  };

  const renderTable = () => (
    <div className={classes.tableWrapper}>
      <table className={classes.excelTable}>
        <thead>
          {/* Cabeçalho — usa a primeira linha do arquivo (modelo) */}
          <tr className={classes.headerRow}>
            <th className={classes.checkboxCell}>
              <input
                type="checkbox"
                className={classes.cyberpunkCheckbox}
                checked={allVisibleSelected}
                ref={(el) => {
                  if (el) el.indeterminate = !allVisibleSelected && Object.keys(selectedRows).length > 0;
                }}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            </th>
            {columns.map((col, i) => (
              <th key={col.key}>{rows[0]?.[i] ?? col.name}</th>
            ))}
          </tr>
        </thead>

        <tbody>
          {filteredDataRows.map(({ row, originalIndex }) => (
            <tr
              key={originalIndex}
              className={`${classes.dataRow} ${selectedRows[originalIndex] ? classes.selectedRow : ""}`}
            >
              <td className={classes.checkboxCell}>
                <input
                  type="checkbox"
                  className={classes.cyberpunkCheckbox}
                  checked={!!selectedRows[originalIndex]}
                  onChange={() =>
                    setSelectedRows((prev) => ({
                      ...prev,
                      [originalIndex]: !prev[originalIndex],
                    }))
                  }
                />
              </td>
              {row.map((cell, ci) => {
                const text = String(cell || "");
                const truncated = truncate(text);
                return (
                  <td key={ci}>
                    {text.length > MAX_CELL_CHARS ? (
                      <Tooltip title={text} arrow placement="top-start">
                        <span className={classes.cellText}>{truncated}</span>
                      </Tooltip>
                    ) : (
                      <span className={classes.cellText}>{text}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
          {filteredDataRows.length === 0 && (
            <tr>
              <td colSpan={(columns?.length || 0) + 1} style={{ textAlign: "center", padding: 20, color: "#999" }}>
                {activeFilterCount > 0 ? t("contactImport.filters.noResults") : t("contactImport.messages.processing")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderContent = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%", minHeight: 0 }}>
      <div className={classes.importOptions}>
        <span style={{ fontSize: 13, fontWeight: 500, color: isDark ? "#e9edef" : "#0f172a" }}>
          {t("contactImport.buttons.validateWhatsApp")}
        </span>
        <CustomToggleSwitch
          checked={validateContact}
          onChange={(e) => setValidateContact(e.target.checked)}
        />
      </div>
      {renderTable()}
      <div className={classes.actions}>
        {uploading && <div style={{ color: isDark ? "#8696a0" : "#667781", fontSize: 13 }}>{t("contactImport.messages.importing")}</div>}
        <Button
          variant="contained"
          color="primary"
          disabled={uploading}
          onClick={() => processImport()}
          style={{ borderRadius: 12 }}
        >
          {t("contactImport.buttons.importContacts")}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={uploading}
          onClick={() => {
            setRows(null);
            setColumns(null);
            setColumnFilters({});
          }}
          style={{ borderRadius: 12 }}
        >
          {t("contactImport.buttons.cancel")}
        </Button>
        {error && <div style={{ color: "#ef4444", fontSize: 13 }}>{error}</div>}
      </div>
    </div>
  );

  const handleCloseImport = () => history.push("/contacts");

  const handleDownloadModel = () => {
    const sample = {
      [t("contactImport.fields.name")]: t("contactImport.sampleContact.name"),
      [t("contactImport.fields.number")]: t("contactImport.sampleContact.number"),
      [t("contactImport.fields.email")]: t("contactImport.sampleContact.email"),
      [t("contactImport.fields.tags")]: t("contactImport.sampleContact.tags"),
      [t("contactImport.fields.followUp")]: t("contactImport.sampleContact.followUp"),
      [t("contactImport.fields.productName")]: t("contactImport.sampleContact.productName"),
      [t("contactImport.fields.productStatus")]: t("contactImport.sampleContact.productStatus"),
    };
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet([sample]);
    XLSX.utils.book_append_sheet(wb, ws, t("contactImport.sheetName"));
    XLSX.writeFile(wb, "modelo_contatos.xlsx");
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onChangeFile,
    maxFiles: 1,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%", overflow: "hidden" }}>
      {imported && (
        <div style={{ padding: 16 }}>
          <ul>
            <li>{countCreated} {t("contactImport.messages.contactsCreated")}</li>
            <li>{countIgnored} {t("contactImport.messages.contactsIgnored")}</li>
          </ul>
        </div>
      )}
      {openingFile && <div style={{ padding: 16 }}>{t("contactImport.messages.processing")}</div>}
      {invalidFile && <div style={{ padding: 16, color: "red" }}>{t("contactImport.messages.invalidFile")}</div>}
      {!imported && rows && columns ? (
        renderContent()
      ) : (
        <>
          <div
            {...getRootProps()}
            style={{
              borderRadius: 20,
              maxWidth: 540,
              margin: "32px auto 20px",
              border: `2px dashed ${isDark ? "rgba(255,255,255,0.18)" : "rgba(15,23,42,0.18)"}`,
              padding: "40px 24px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              gap: 14,
              cursor: "pointer",
              background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)",
              transition: "background 0.2s, border-color 0.2s",
            }}
          >
            <div style={{
              width: 88,
              height: 88,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: isDark ? "rgba(33,150,243,0.12)" : "rgba(33,150,243,0.08)",
              color: theme.palette.primary.main,
            }}>
              <UploadCloud size={42} strokeWidth={1.6} />
            </div>
            <h5 style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
              color: isDark ? "#e9edef" : "#0f172a",
            }}>
              {t("contactImport.dropzone.clickOrDrag")}
            </h5>
            <p style={{
              margin: 0,
              fontSize: 12.5,
              color: isDark ? "#ef9a9a" : "#c0392b",
              fontWeight: 500,
              textAlign: "center",
              maxWidth: 420,
              lineHeight: 1.5,
            }}>
              {t("contactImport.dropzone.importantNote")}
            </p>
          </div>
          <input {...getInputProps()} />
          <div className={classes.backButtonContainer}>
            <Button variant="contained" color="primary" onClick={handleDownloadModel} style={{ marginRight: 10, borderRadius: 12 }}>
              {t("contactImport.buttons.downloadModel")}
            </Button>
            <Button variant="contained" color="secondary" disabled={uploading} onClick={handleCloseImport} style={{ borderRadius: 12 }}>
              {t("contactImport.buttons.back")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ContactImport;
